import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Switch, Tooltip } from "antd";
import "./app-features-management.scss";
import { LockOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";
import {
  AppFeaturesConstants,
  EssentialPlanColors,
  EssentialPlusPlanColors,
  StartedPlanColors,
  UnsubscribedColors,
  getPlanTabDisplayName,
} from "./constants";
import { useSettings } from "../../custom-hooks/useSettings";
import { MICROSITE_PRO_SETTINGS_UPDATED_EVENT } from "./missingTabsSettingsUtils";
import { useStore } from "../../store";
import { setGeneratingAI } from "../../store/actions";
import RosieAILogo from "../../assets/images/rosie_ai_logo.png";
import RosiePopup from "./RosiePopup";
import uuid from "react-uuid";
import moment from "moment";
import {
  clearRosiePendingFlags,
  markRosiePromptShown,
} from "../../utils/rosieNewSite";

const AppFeaturesManagement = ({
  micrositeData,
  micrositeId,
  shouldOpenRosie = false,
  showSubscriptionModal,
  setShowSubscriptionModal,
  setDidProSettingsUpdate,
}) => {
  const { updateSettings } = useSettings();
  const { dispatch, generatingAI, microSites } = useStore();
  const navigate = useNavigate();
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const generatingRef = useRef(false);
  const [startedData, setStartedData] = useState(null);
  const [essentialData, setEssentialData] = useState(null);
  const [essentialPlusData, setEssentialPlusData] = useState(null);

  const [plansData, setPlansData] = useState(null);
  const [, setAllChecker] = useState(false);
  const [currentSubscribed, setCurrentSubscribed] = useState("");
  const [, setOrganizationData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [rosiePopup, setRosiePopup] = useState(false);
  const [suppressRosiePopupAfterAi, setSuppressRosiePopupAfterAi] = useState(
    () => localStorage.getItem("rosieAiComplete") === "true",
  );
  const [missingTabsModalVisible, setMissingTabsModalVisible] = useState(
    () => localStorage.getItem("rosieAiComplete") === "true",
  );
  const [rosiePopupMountKey, setRosiePopupMountKey] = useState(0);
  const [cancelPlanLoading, setCancelPlanLoading] = useState(false);
  const [isCancelPlanModalOpen, setIsCancelPlanModalOpen] = useState(false);
  const progressIntervalRef = useRef(null);
  const isAiCompletingRef = useRef(false);
  // Sync guard so progress loop cannot reopen Rosie after Site Tabs opens
  const siteTabsOpenRef = useRef(
    localStorage.getItem("rosieAiComplete") === "true",
  );
  // Persist dismiss so remounts (e.g. after setDidProSettingsUpdate) don't reopen
  const userDismissedRosieRef = useRef(
    sessionStorage.getItem("rosieProgressDismissed") === "true",
  );
  const connectedAccountId = sessionStorage.getItem("connectedAccountId");
  const isNewlyCreatedMicroSite =
    localStorage?.getItem("isNewlyCreatedMicroSite") === "true";

  const [aiDetailsSessionData, setAiDetailsSessionData] = useState(() => {
    const value = sessionStorage.getItem("aiDetails");
    return value ? JSON.parse(value) : null;
  });

  const clearProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const markRosieDismissed = () => {
    userDismissedRosieRef.current = true;
    sessionStorage.setItem("rosieProgressDismissed", "true");
  };

  const clearRosieDismissed = () => {
    userDismissedRosieRef.current = false;
    sessionStorage.removeItem("rosieProgressDismissed");
  };

  // Wrap setter so X/cancel actually sticks (progress effect won't force-reopen)
  const handleSetRosiePopup = (open) => {
    if (!open) {
      markRosieDismissed();
      clearProgressInterval();
    }
    setRosiePopup(open);
  };

  const isRosieDismissed = () =>
    userDismissedRosieRef.current ||
    sessionStorage.getItem("rosieProgressDismissed") === "true";

  const isRosieAiComplete = () =>
    localStorage.getItem("rosieAiComplete") === "true";

  // Site Tabs modal open (App.jsx) or AI completed → never show Rosie progress
  const shouldHideRosieForSiteTabs =
    generatingAI?.hideRosiePopup === true ||
    missingTabsModalVisible ||
    suppressRosiePopupAfterAi ||
    isRosieAiComplete();

  const getRosieAiSessionForPopup = () => {
    if (!aiDetailsSessionData) return null;
    if (aiDetailsSessionData.micrositeID !== micrositeId) return null;
    return aiDetailsSessionData;
  };

  const forceHideRosiePopup = () => {
    siteTabsOpenRef.current = true;
    isAiCompletingRef.current = true;
    setRosiePopup(false);
    setProgress(100);
    markRosieDismissed();
    clearProgressInterval();
    setRosiePopupMountKey((key) => key + 1);
  };

  const closeRosieAfterAiComplete = (aiDetails = null) => {
    siteTabsOpenRef.current = true;
    isAiCompletingRef.current = true;
    setMissingTabsModalVisible(true);
    setSuppressRosiePopupAfterAi(true);
    markRosieDismissed();
    clearProgressInterval();
    setProgress(100);
    if (aiDetails) {
      setAiDetailsSessionData(aiDetails);
    }
    clearRosiePendingFlags();
    markRosiePromptShown(micrositeId);
    setShowSubscriptionModal(false);
    forceHideRosiePopup();
  };

  const getCanonicalTabName = (displayName = "") => {
    const name = displayName.toLowerCase().replace(/&/g, "and").trim();
    if (name.includes("map advert")) return "map advert";
    if (
      name.includes("video carousel") ||
      name.includes("app carousel") ||
      name === "app"
    ) {
      return "video carousel";
    }
    if (name.includes("about us") || name === "about") return "about us";
    if (name.includes("contact us") || name === "contact") return "contact us";
    if (name.includes("team")) return "teams & roles";
    if (name.includes("experience")) return "experiences of work";
    if (name.includes("product")) return "products";
    if (name.includes("service")) return "services";
    if (name.includes("event")) return "events";
    if (name.includes("course")) return "courses";
    if (name.includes("contract")) return "contracts";
    if (name.includes("why work")) return "why work for us";
    if (name.includes("vacanc")) return "vacancies";
    if (name.includes("apprentice")) return "apprenticeships";
    if (name.includes("blog") || name.includes("news")) return "blog";
    if (name.includes("press")) return "in the press";
    return name;
  };

  const STARTER_TAB_ORDER = [
    "map advert",
    "video carousel",
    "about us",
    "contact us",
    "teams & roles",
    "experiences of work",
  ];
  const ESSENTIAL_TAB_ORDER = [
    "products",
    "services",
    "events",
    "courses",
    "contracts",
  ];
  const ESSENTIAL_PLUS_TAB_ORDER = [
    "why work for us",
    "vacancies",
    "apprenticeships",
    "blog",
    "in the press",
  ];

  const sortTabsData = (list, orderList) => {
    if (!list) return [];
    return [...list].sort((a, b) => {
      const aIndex = orderList.indexOf(getCanonicalTabName(a?.displayName));
      const bIndex = orderList.indexOf(getCanonicalTabName(b?.displayName));
      const aOrder = aIndex === -1 ? orderList.length : aIndex;
      const bOrder = bIndex === -1 ? orderList.length : bIndex;
      return aOrder - bOrder;
    });
  };

  const handleToggle = async (plan, item) => {
    const updateTabsData = (tabsData) => {
      const data = tabsData?.map((dt) => {
        if (dt?.id === item?.id) {
          return {
            ...dt,
            secondaryToggle: !dt?.secondaryToggle,
          };
        } else {
          return dt;
        }
      });
      return data;
    };

    let planData = [...plansData];
    if (plan === AppFeaturesConstants.STARTER) {
      const updatedData = {
        ...startedData,
        tabs: updateTabsData(startedData?.tabs),
      };
      planData = plansData?.map((dt) =>
        dt?.plan === AppFeaturesConstants.STARTER ? updatedData : dt,
      );
      setStartedData(updatedData);
    } else if (plan === AppFeaturesConstants.ESSENTIALS) {
      const updatedData = {
        ...essentialData,
        tabs: updateTabsData(essentialData?.tabs),
      };
      planData = plansData?.map((dt) =>
        dt?.plan === AppFeaturesConstants.ESSENTIALS ? updatedData : dt,
      );
      setEssentialData(updatedData);
    } else if (plan === AppFeaturesConstants.ESSENTIALS_PLUS) {
      const updatedData = {
        ...essentialPlusData,
        tabs: updateTabsData(essentialPlusData?.tabs),
      };
      planData = plansData?.map((dt) =>
        dt?.plan === AppFeaturesConstants.ESSENTIALS_PLUS ? updatedData : dt,
      );
      setEssentialPlusData(updatedData);
    }
    setPlansData(planData);

    const payload = [
      {
        id: item?.id,
        secondaryToggle: !item?.secondaryToggle,
      },
    ];
    await updateSettings(micrositeId, payload);
    setDidProSettingsUpdate((prev) => !prev);
  };


  const handleFetchPlans = async () => {
    if (!micrositeId) {
      return;
    }
    const siteExists = Array.isArray(microSites)
      ? microSites.some((site) => String(site.id) === String(micrositeId))
      : true;
    if (!siteExists) {
      return;
    }
    try {
      const response = await getRequest(
        userServiceApi.handleFetchPlans(micrositeId),
      );
      const data = response?.data;
      setResponseData(data);
      if (data?.planDetails?.length > 0) {
        const cycleForTabs = (planName) =>
          data.planDetails.find(
            (entry) =>
              entry?.plan === planName &&
              entry?.subscriptionCycle === "MONTHLY",
          ) || data.planDetails.find((entry) => entry?.plan === planName);

        const isPlanSubscribedInDetails = (planName) =>
          data.planDetails.some(
            (entry) => entry?.plan === planName && entry?.subscribed === true,
          );

        const starterPlan = cycleForTabs(AppFeaturesConstants.STARTER);
        const essentialsPlan = cycleForTabs(AppFeaturesConstants.ESSENTIALS);
        const essentialsPlusPlan = cycleForTabs(
          AppFeaturesConstants.ESSENTIALS_PLUS,
        );

        const order = ["STARTER", "ESSENTIALS", "ESSENTIALS_PLUS"];
        const sortedData = data?.planDetails?.sort(
          (a, b) => order?.indexOf(a?.plan) - order?.indexOf(b?.plan),
        );

        let subscribedPlan = "";
        sortedData?.forEach((dt) => {
          if (dt?.subscribed) {
            subscribedPlan = dt?.plan;
          }
        });
        setPlansData(data?.planDetails);
        setCurrentSubscribed(subscribedPlan);
        setStartedData({
          ...starterPlan,
          subscribed: isPlanSubscribedInDetails(AppFeaturesConstants.STARTER),
          tabs: sortTabsData(starterPlan?.tabs, STARTER_TAB_ORDER),
        });
        setEssentialData({
          ...essentialsPlan,
          subscribed: isPlanSubscribedInDetails(AppFeaturesConstants.ESSENTIALS),
          tabs: sortTabsData(essentialsPlan?.tabs, ESSENTIAL_TAB_ORDER),
        });
        setEssentialPlusData({
          ...essentialsPlusPlan,
          subscribed: isPlanSubscribedInDetails(
            AppFeaturesConstants.ESSENTIALS_PLUS,
          ),
          tabs: sortTabsData(essentialsPlusPlan?.tabs, ESSENTIAL_PLUS_TAB_ORDER),
        });
      }
    } catch (error) {
      toast.error(error?.error);
    }
  };

  useEffect(() => {
    handleFetchPlans()
  },[])

  const handleCloseCancelPlanModal = () => {
    setIsCancelPlanModalOpen(false);
  };

  const handleCancelPlan = async () => {
    const payload = {
      connectedAccountId: connectedAccountId,
      micrositeId: micrositeId,
    };
    try {
      setCancelPlanLoading(true);
      const response = await postRequest(
        userServiceApi.handleCancelSubscription(micrositeId),
        payload,
      );
      if (response) {
        toast.success("Your plan has been cancelled successfully.");
        setIsCancelPlanModalOpen(false);
        setDidProSettingsUpdate?.(true);
        handleFetchPlans();
      }
    } catch (error) {
      toast.error(error?.error || error);
    } finally {
      setCancelPlanLoading(false);
    }
  };

  useEffect(() => {
    const isAllTrue = plansData?.every((p) =>
      p?.tabs?.every((t) => t?.secondaryToggle),
    );
    setAllChecker(isAllTrue);
  }, [plansData]);

  useEffect(() => {
    fetchOrganization();
    if (micrositeId) {
      handleFetchPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micrositeId]);

  useEffect(() => {
    const onProSettingsUpdated = (event) => {
      const updatedMicrositeId = event?.detail?.micrositeId;
      if (
        updatedMicrositeId &&
        micrositeId &&
        String(updatedMicrositeId) === String(micrositeId)
      ) {
        handleFetchPlans();
      }
    };

    window.addEventListener(
      MICROSITE_PRO_SETTINGS_UPDATED_EVENT,
      onProSettingsUpdated,
    );
    return () => {
      window.removeEventListener(
        MICROSITE_PRO_SETTINGS_UPDATED_EVENT,
        onProSettingsUpdated,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micrositeId]);

  const updateAiDetails = (data) => {
    sessionStorage.setItem("aiDetails", JSON.stringify(data));
    setAiDetailsSessionData(data);
  };


  const handleGenerateAIApi = async () => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setIsBtnDisabled(true);
    const jobId = uuid();
    try {
      const response = await postRequest(
        `${userServiceApi.generateApiData(micrositeId)}&jobId=${jobId}`,
      );

      if (response) {
        siteTabsOpenRef.current = false;
        isAiCompletingRef.current = false;
        localStorage.removeItem("rosieAiComplete");
        setMissingTabsModalVisible(false);
        setSuppressRosiePopupAfterAi(false);
        clearRosieDismissed();
        setProgress(0);
        updateAiDetails({
          aiStatus: "inprogress",
          micrositeID: micrositeId,
          isBtnDisabled: true,
          isGeneratingData: true,
          jobId,
        });

        dispatch(
          setGeneratingAI({
            ...generatingAI,
            jobId,
            hideRosiePopup: false,
          }),
        );

        toast.success(response || "AI data is generating!");
        setDidProSettingsUpdate((prev) => !prev);
        clearRosiePendingFlags();
        markRosiePromptShown(micrositeId);
        // Keep RosiePopup open so the progress bar can show while AI generates
        setRosiePopup(true);
        setIsBtnDisabled(false);
      } else {
        updateAiDetails({
          aiStatus: "failed",
          micrositeID: micrositeId,
          isBtnDisabled: false,
          isGeneratingData: false,
        });
        setIsBtnDisabled(false);
        toast.error("Something went wrong");
      }
    } catch (error) {
      updateAiDetails({
        aiStatus: "failed",
        micrositeID: micrositeId,
        isBtnDisabled: false,
        isGeneratingData: false,
      });
      setIsBtnDisabled(false);
      toast.error(error?.message || "Something went wrong");
    } finally {
      generatingRef.current = false;
    }
  };

  const fetchOrganization = async () => {
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const response = await getRequest(
        userServiceApi?.getSingleOrganization(),
        { headers },
      );

      if (response && response?.data) {
        setOrganizationData(response?.data);
      }
    } catch (error) {
      toast.error(error?.error);
    }
  };

  // When App.jsx opens Site Tabs modal, always close Rosie (progress ignored)
  useEffect(() => {
    if (!generatingAI?.hideRosiePopup) return;

    siteTabsOpenRef.current = true;
    setMissingTabsModalVisible(true);
    setSuppressRosiePopupAfterAi(true);
    forceHideRosiePopup();
    setAiDetailsSessionData((prev) =>
      prev
        ? { ...prev, isGeneratingData: false, aiStatus: "completed" }
        : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatingAI?.hideRosiePopup]);

  useEffect(() => {
    // MissingTabsModal open / AI done → stop progress loop and keep Rosie closed
    if (shouldHideRosieForSiteTabs || siteTabsOpenRef.current) {
      clearProgressInterval();
      setRosiePopup(false);
      setProgress(100);
      return;
    }

    const isGeneratingForThisSite =
      aiDetailsSessionData?.micrositeID === micrositeId &&
      aiDetailsSessionData?.isGeneratingData;

    if (
      isGeneratingForThisSite &&
      !isAiCompletingRef.current &&
      !siteTabsOpenRef.current &&
      !isRosieDismissed()
    ) {
      setRosiePopup(true);

      // Keep a single 0→90 loop running until websocket completion / Site Tabs open
      if (!progressIntervalRef.current) {
        progressIntervalRef.current = setInterval(() => {
          // Site Tabs / AI complete → kill loop and force Rosie closed
          if (isAiCompletingRef.current || siteTabsOpenRef.current) {
            clearProgressInterval();
            setProgress(100);
            setRosiePopup(false);
            return;
          }
          setProgress((prev) => {
            if (prev >= 90) return 0;
            return prev + 10;
          });
        }, 3000);
      }
      return;
    }

    // Still generating but user closed the popup — keep interval stopped from reopening UI
    if (isGeneratingForThisSite && isRosieDismissed()) {
      return;
    }

    if (!isAiCompletingRef.current) {
      clearProgressInterval();
    }
  }, [
    aiDetailsSessionData?.micrositeID,
    aiDetailsSessionData?.isGeneratingData,
    micrositeId,
    shouldHideRosieForSiteTabs,
  ]);

  useEffect(() => {
    const onMissingTabsOpen = () => {
      // Always close Rosie when MissingTabsModal opens (progress complete or not)
      siteTabsOpenRef.current = true;
      isAiCompletingRef.current = true;
      setMissingTabsModalVisible(true);
      setSuppressRosiePopupAfterAi(true);
      clearProgressInterval();
      setProgress(100);
      setRosiePopup(false);
      forceHideRosiePopup();
      setAiDetailsSessionData((prev) =>
        prev
          ? { ...prev, isGeneratingData: false, aiStatus: "completed" }
          : prev,
      );
    };

    const onMissingTabsClose = (event) => {
      const allowNewRosiePrompt = event?.detail?.allowNewRosiePrompt === true;

      setMissingTabsModalVisible(false);
      clearProgressInterval();
      setProgress(0);
      setRosiePopup(false);
      setAiDetailsSessionData((prev) =>
        prev ? { ...prev, isGeneratingData: false } : null,
      );

      if (allowNewRosiePrompt) {
        // New microsite flow — allow Rosie YES/NO again
        siteTabsOpenRef.current = false;
        isAiCompletingRef.current = false;
        setSuppressRosiePopupAfterAi(false);
        clearRosieDismissed();
        dispatch(
          setGeneratingAI({
            ...generatingAI,
            hideRosiePopup: false,
          }),
        );
      } else {
        // Site Tabs cancel — Rosie must stay closed
        siteTabsOpenRef.current = true;
        isAiCompletingRef.current = true;
        setSuppressRosiePopupAfterAi(true);
        markRosieDismissed();
      }
    };

    window.addEventListener("missing-tabs-modal-open", onMissingTabsOpen);
    window.addEventListener("missing-tabs-modal-close", onMissingTabsClose);

    return () => {
      window.removeEventListener("missing-tabs-modal-open", onMissingTabsOpen);
      window.removeEventListener("missing-tabs-modal-close", onMissingTabsClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleUpdate = (event) => {
      const status = event?.detail?.aiStatus?.toLowerCase();

      if (status === "completed") {
        // Websocket done → close progress popup immediately so it is not
        // left behind MissingTabsModal (opened in App.jsx on the same event).
        localStorage.setItem("rosieAiComplete", "true");
        siteTabsOpenRef.current = true;
        closeRosieAfterAiComplete(event?.detail);

        setTimeout(() => {
          setDidProSettingsUpdate((prev) => !prev);
          sessionStorage.removeItem("aiDetails");
          // Keep progress at 100 and Rosie closed while Site Tabs can still be open
          setProgress(100);
          setRosiePopup(false);
        }, 500);
        return;
      }

      // failed (or any non-completed terminal status)
      isAiCompletingRef.current = false;
      markRosieDismissed();
      clearProgressInterval();
      setAiDetailsSessionData(event?.detail);
      setProgress(0);
      clearRosiePendingFlags();
      markRosiePromptShown(micrositeId);
      setShowSubscriptionModal(false);
      setRosiePopup(false);
      setDidProSettingsUpdate((prev) => !prev);
      sessionStorage.removeItem("aiDetails");
      sessionStorage.removeItem("rosieProgressDismissed");
    };

    window.addEventListener("ai-status-updated", handleUpdate);

    return () => {
      window.removeEventListener("ai-status-updated", handleUpdate);
      clearProgressInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Rosie only when parent signals a manual accordion open (shouldOpenRosie).
    // Do not open from refresh / default first-accordion auto-open.
    if (!showSubscriptionModal || !shouldOpenRosie) return;

    localStorage.removeItem("rosieAiComplete");
    siteTabsOpenRef.current = false;
    setMissingTabsModalVisible(false);
    setSuppressRosiePopupAfterAi(false);
    clearRosieDismissed();
    isAiCompletingRef.current = false;
    setProgress(0);
    dispatch(
      setGeneratingAI({
        ...generatingAI,
        hideRosiePopup: false,
      }),
    );
    const sessionAi = JSON.parse(sessionStorage.getItem("aiDetails") || "null");
    if (
      !sessionAi?.isGeneratingData ||
      sessionAi?.micrositeID !== micrositeId
    ) {
      sessionStorage.removeItem("aiDetails");
      setAiDetailsSessionData(null);
    }
    // Remount RosiePopup so closedForMissingTabs / prior dismiss state resets.
    setRosiePopupMountKey((key) => key + 1);
    setRosiePopup(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubscriptionModal, shouldOpenRosie, micrositeId]);

  const rawDaysRemaining = responseData?.daysRemaining;
  const trialDaysRemaining =
    rawDaysRemaining != null && rawDaysRemaining !== ""
      ? Math.max(0, parseInt(rawDaysRemaining, 10) || 0)
      : null;
  const trialEndDateFormatted =
    trialDaysRemaining != null && trialDaysRemaining > 0
      ? moment().add(trialDaysRemaining, "days").format("DD-MM-YYYY")
      : "";
  const isPlanSubscribed = (planName) =>
    responseData?.planDetails?.some(
      (entry) => entry?.plan === planName && entry?.subscribed === true,
    ) ?? false;
  const anyPlanSubscribed = [
    AppFeaturesConstants.STARTER,
    AppFeaturesConstants.ESSENTIALS,
    AppFeaturesConstants.ESSENTIALS_PLUS,
  ].some(isPlanSubscribed);
  const isPlanSubscribeCtaEnabled = (planName) => {
    if (trialDaysRemaining != null) {
      return trialDaysRemaining < 5;
    }
    if (!anyPlanSubscribed) {
      return true;
    }
    return !isPlanSubscribed(planName);
  };
  const shouldShowTrialExpiryNote =
    trialDaysRemaining != null &&
    trialDaysRemaining > 0 &&
    trialDaysRemaining < 5;
  const canCancelPlan =
    trialDaysRemaining != null && trialDaysRemaining >= 14;

  const handleSubscribeClick = () => {
    clearRosiePendingFlags();
    markRosiePromptShown(micrositeId);
    setShowSubscriptionModal(false);
    navigate(`/app-feature-plans/${micrositeId}`);
  };

  return (
    <>
      <Modal
        open={showSubscriptionModal}
        width="70%"
        style={{ top: "40px" }}
        closable={true}
        onCancel={() => {
          clearRosiePendingFlags();
          markRosiePromptShown(micrositeId);
          setShowSubscriptionModal(false);
        }}
        footer={null}
        className="app-features-management-container"
        centered={true}
        maskClosable={false}
      >
        <div className="app-features-management">
          <div className="site-feature-management-header-row">
            <div className="app-features-management-header">
              <div className="header-left-side" style={{ textAlign: "center" }}>
                <div className="header-title">Site Feature Management</div>
                <span className="header-description">
                  {trialDaysRemaining > 0
                    ? `Free to Create/Edit and Free to Advertise for ${trialDaysRemaining} Days`
                    : "Free to Create/Edit and Free to Advertise"}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="management-cancel-plan-btn"
              disabled={!canCancelPlan || cancelPlanLoading}
              onClick={() => setIsCancelPlanModalOpen(true)}
            >
              Cancel
            </button>
          </div>

          {!rosiePopup && isNewlyCreatedMicroSite && (
            <div className="header-right-side">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
             
{responseData?.aiGenerated === false && (
                  <>
                    <div className="rosie-ai-logo">
                      <img
                        src={RosieAILogo}
                        alt="Generate AI"
                        onClick={() => {
                          if (
                            !aiDetailsSessionData?.isBtnDisabled &&
                            !isNewlyCreatedMicroSite
                          ) {
                            handleGenerateAIApi();
                          } else {
                            setRosiePopup(true);
                          }
                        }}
                      />
                    </div>
                    <span
                      style={{
                        color: "#cc0e2d",
                        fontSize: "20px",
                        cursor: "pointer",
                      }}
                    >
                      <Tooltip
                        placement="bottom"
                        className="generate-ai-tooltip"
                        title="Rosie is designed to help you draft your site but how much she can help will depend on your website so empty tabs may appears"
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="app-features-management-content">
            {/* Starter Plan  */}
            <div
              className="app-features-card-item"
              style={{
                background:
                  currentSubscribed === AppFeaturesConstants.STARTER
                    ? StartedPlanColors.cardBg
                    : UnsubscribedColors.cardBg,
                border: `1px solid ${currentSubscribed === AppFeaturesConstants.STARTER ? StartedPlanColors.border : UnsubscribedColors.border}`,
              }}
            >
              <div
                className="card-item-header"
                style={{
                  background:
                    currentSubscribed === AppFeaturesConstants.STARTER
                      ? StartedPlanColors.headerBg
                      : UnsubscribedColors.headerBg,
                  borderBottom: `1px solid ${currentSubscribed === AppFeaturesConstants.STARTER ? StartedPlanColors.border : UnsubscribedColors.border}`,
                }}
              >
                <div
                  className="header-text"
                  style={{
                    color:
                      currentSubscribed === AppFeaturesConstants.STARTER
                        ? StartedPlanColors.headerText
                        : UnsubscribedColors.headerText,
                  }}
                >
                  Starter{" "}
                  {/* {currentSubscribed === AppFeaturesConstants.STARTER && (
                  <text style={{ fontSize: "16px", fontWeight: "400" }}>
                    ({AppFeaturesConstants.CURRENT_PLAN})
                  </text>
                )} */}
                </div>
                <span style={{ visibility: "hidden" }}>
                  Get started with essential features
                </span>
              </div>

              <div
                className="card-item-content"
                style={{
                  background:
                    currentSubscribed === AppFeaturesConstants.STARTER
                      ? "transparent"
                      : UnsubscribedColors.cardBg,
                }}
              >
                {startedData?.tabs?.map((item, index) => {
                  const isMandatoryStarterTab = index < 4;
                  return (
                    <>
                      <div
                        className="card-items-menu"
                        key={`starter-${item?.id}`}
                      >
                        {getPlanTabDisplayName(item?.displayName)}
                        {isMandatoryStarterTab ? null : !startedData?.subscribed ? (
                          <div className="card-item-lock-icon">
                            <Tooltip
                              className="lock-icon-tooltip"
                              placement="top"
                              title="Upgrade to Starter to enable this feature"
                            >
                              <LockOutlined />
                            </Tooltip>
                          </div>
                        ) : item?.tabType !==
                          AppFeaturesConstants.MANDATORY_TABS ? (
                          <div>
                            <span className="card-item-switch">
                              <Switch
                                checked={item?.secondaryToggle}
                                onClick={() =>
                                  handleToggle(
                                    AppFeaturesConstants.STARTER,
                                    item,
                                  )
                                }
                                className={
                                  item?.secondaryToggle
                                    ? "active-switch"
                                    : "inactive-switch"
                                }
                              />
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </>
                  );
                })}
              </div>

              {/* {shouldShowSubscribeCta && ( */}
              <div className="card-btn">
                <button
                  // disabled={true}
                  disabled={
                    !isPlanSubscribeCtaEnabled(AppFeaturesConstants.STARTER)
                  }

                  className="card-item-button"
                  style={{
                    color: StartedPlanColors.btnText,
                    background: StartedPlanColors.btnBg,
                    border: `1px solid ${StartedPlanColors.border}`,
                    cursor: "pointer",
                    // cursor: "not-allowed",
                    cursor: isPlanSubscribeCtaEnabled(
                      AppFeaturesConstants.STARTER,
                    )
                      ? "pointer"
                      : "not-allowed",
                  }}
                  onClick={handleSubscribeClick}
                >
                  Subscribe to keep online
                </button>
              </div>
            </div>

            {/* Essential Plan  */}
            <div
              className="app-features-card-item"
              style={{
                background:
                  currentSubscribed === AppFeaturesConstants.ESSENTIALS
                    ? EssentialPlanColors.cardBg
                    : UnsubscribedColors.cardBg,
                border: `1px solid ${currentSubscribed === AppFeaturesConstants.ESSENTIALS ? EssentialPlanColors.border : UnsubscribedColors.border}`,
              }}
            >
              <div
                className="card-item-header"
                style={{
                  background:
                    currentSubscribed === AppFeaturesConstants.ESSENTIALS
                      ? EssentialPlanColors.headerBg
                      : UnsubscribedColors.headerBg,
                  borderBottom: `1px solid ${currentSubscribed === AppFeaturesConstants.ESSENTIALS ? EssentialPlanColors.border : UnsubscribedColors.border}`,
                }}
              >
                <div
                  className="header-text"
                  style={{
                    color:
                      currentSubscribed === AppFeaturesConstants.ESSENTIALS
                        ? EssentialPlanColors.headerText
                        : UnsubscribedColors.headerText,
                  }}
                >
                  Essential{" "}
                  {/* {currentSubscribed === AppFeaturesConstants.ESSENTIALS && (
                  <text style={{ fontSize: "16px", fontWeight: "400" }}>
                    ({AppFeaturesConstants.CURRENT_PLAN})
                  </text>
                )} */}
                </div>
                <span>Everything in Starter plus B2B/C Features</span>
              </div>

              <div
                className="card-item-content"
                style={{
                  background:
                    currentSubscribed === AppFeaturesConstants.ESSENTIALS
                      ? "transparent"
                      : UnsubscribedColors.cardBg,
                }}
              >
                {essentialData?.tabs?.map((item) => {
                  return (
                    <>
                      <div
                        className="card-items-menu"
                        key={`essential-${item?.id}`}
                      >
                        {getPlanTabDisplayName(item?.displayName)}
                        {!essentialData?.subscribed ? (
                          <div className="card-item-lock-icon">
                            <Tooltip
                              className="lock-icon-tooltip"
                              placement="top"
                              title="Upgrade to Essentials to enable this feature"
                            >
                              <LockOutlined />
                            </Tooltip>
                          </div>
                        ) : item?.mandatory !==
                          AppFeaturesConstants.MANDATORY_TABS ? (
                          <div>
                            <span className="card-item-switch">
                              <Switch
                                checked={item?.secondaryToggle}
                                onClick={() =>
                                  handleToggle(
                                    AppFeaturesConstants.ESSENTIALS,
                                    item,
                                  )
                                }
                                className={
                                  item?.secondaryToggle
                                    ? "active-switch"
                                    : "inactive-switch"
                                }
                              />
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </>
                  );
                })}
              </div>

              <div className="card-btn">
                <button
                  // disabled={true}
                  disabled={
                    !isPlanSubscribeCtaEnabled(AppFeaturesConstants.ESSENTIALS)
                  }

                  className="card-item-button"
                  style={{
                    color: EssentialPlanColors.btnText,
                    background: EssentialPlanColors.btnBg,
                    border: `1px solid ${EssentialPlanColors.border}`,
                    cursor: isPlanSubscribeCtaEnabled(
                      AppFeaturesConstants.ESSENTIALS,
                    )
                      ? "pointer"
                      : "not-allowed",
                  }}
                  onClick={handleSubscribeClick}
                >
                  {/* {currentSubscribed === AppFeaturesConstants.ESSENTIALS
                      ? "Current Plan"
                      : "Subscribe to keep online"} */}
                  Subscribe to keep online
                </button>
              </div>
            </div>

            {/* Essential Plus Plan*/}
            <div
              className="app-features-card-item"
              style={{
                background:
                  currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS
                    ? EssentialPlusPlanColors.cardBg
                    : UnsubscribedColors.cardBg,
                border: `1px solid ${currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS ? EssentialPlusPlanColors.border : UnsubscribedColors.border}`,
              }}
            >
              <div
                className="card-item-header"
                style={{
                  background:
                    currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS
                      ? EssentialPlusPlanColors.headerBg
                      : UnsubscribedColors.headerBg,
                  borderBottom: `1px solid ${currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS ? EssentialPlusPlanColors.border : UnsubscribedColors.border}`,
                }}
              >
                <div
                  className="header-text"
                  style={{
                    color:
                      currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS
                        ? EssentialPlusPlanColors.headerText
                        : UnsubscribedColors.headerText,
                  }}
                >
                  Essential Plus{" "}
                  {/* {currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS && (
                  <text style={{ fontSize: "16px", fontWeight: "400" }}>
                    ({AppFeaturesConstants.CURRENT_PLAN})
                  </text>
                )} */}
                </div>
                <span>Everything in Essential plus HR/PR Features</span>
              </div>

              <div
                className="card-item-content"
                style={{
                  background:
                    currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS
                      ? "transparent"
                      : UnsubscribedColors.cardBg,
                }}
              >
                {essentialPlusData?.tabs?.map((item) => {
                  return (
                    <>
                      <div
                        className="card-items-menu"
                        key={`essential-plus-${item?.id}`}
                      >
                        {getPlanTabDisplayName(item?.displayName)}
                        {!essentialPlusData?.subscribed ? (
                          <div className="card-item-lock-icon">
                            <Tooltip
                              className="lock-icon-tooltip"
                              placement="top"
                              title="Upgrade to Essentials Plus to enable this feature"
                            >
                              <LockOutlined />
                            </Tooltip>
                          </div>
                        ) : item?.mandatory !==
                          AppFeaturesConstants.MANDATORY_TABS ? (
                          <div>
                            <span className="card-item-switch">
                              <Switch
                                checked={item?.secondaryToggle}
                                onClick={() =>
                                  handleToggle(
                                    AppFeaturesConstants.ESSENTIALS_PLUS,
                                    item,
                                  )
                                }
                                className={
                                  item?.secondaryToggle
                                    ? "active-switch"
                                    : "inactive-switch"
                                }
                              />
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </>
                  );
                })}
              </div>

              <div className="card-btn">
                <button
                  // disabled={true}
                  disabled={
                    !isPlanSubscribeCtaEnabled(
                      AppFeaturesConstants.ESSENTIALS_PLUS,
                    )
                  }

                  className="card-item-button"
                  style={{
                    color: EssentialPlusPlanColors.btnText,
                    background: EssentialPlusPlanColors.btnBg,
                    border: `1px solid ${EssentialPlusPlanColors.border}`,
                    cursor: "pointer",
                    // cursor: "not-allowed",
                    cursor: isPlanSubscribeCtaEnabled(
                      AppFeaturesConstants.ESSENTIALS_PLUS,
                    )
                      ? "pointer"
                      : "not-allowed",

                  }}
                  onClick={handleSubscribeClick}
                >
                  {/* {currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS
                      ? "Current Plan"
                      : "Subscribe to keep all features online"} */}
                  Subscribe to keep online
                </button>
              </div>
            </div>
          </div>

          {shouldShowTrialExpiryNote && (
            <div
              style={{ marginTop: "16px" }}
              className="subscription-expiry-note"
            >
              <strong>⚠ Important : </strong>
              {trialDaysRemaining === 1
                ? `Your free trial ends tomorrow (${trialEndDateFormatted}). `
                : `Your free trial ends on ${trialEndDateFormatted} (${trialDaysRemaining} days remaining). `}
              Please subscribe to keep all features online.
            </div>
          )}
        </div>

        {/* Keep mounted so Ant Design portal closes via open=false (avoids orphan popup) */}
        <RosiePopup
          key={rosiePopupMountKey}
          micrositeId={micrositeId}
          rosiePopup={
            rosiePopup &&
            !shouldHideRosieForSiteTabs &&
            !siteTabsOpenRef.current
          }
          setRosiePopup={handleSetRosiePopup}
          generatingAI={getRosieAiSessionForPopup()}
          progress={progress}
          isNewlyCreatedMicroSite={isNewlyCreatedMicroSite}
          handleGenerateAIApi={handleGenerateAIApi}
          isBtnDisabled={isBtnDisabled}
        />
      </Modal>

      <Modal
        className="confirmation-modal"
        open={isCancelPlanModalOpen}
        footer={null}
        onCancel={handleCloseCancelPlanModal}
        destroyOnClose={true}
        closable={false}
      >
        <div className="message">
          Do you want to cancel the current plan?
        </div>
        <div className="modalBtns">
          <Button className="cancel-btn" onClick={handleCloseCancelPlanModal}>
            Cancel
          </Button>
          <Button
            className="confirm-btn"
            loading={cancelPlanLoading}
            onClick={handleCancelPlan}
          >
            Yes
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default AppFeaturesManagement;
